import type { CountryCode } from '__generated__/graphql-types';

import path from 'js/lib/path';

import type { BreadcrumbLink } from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import type { Breadcrumb } from 'bundles/shared-articles/components/seo/utils/schemaMarkupUtils';
import type {
  ArticleFieldsFragment,
  SeoContentHubNavigationFieldsFragment,
  SeoContentHubNavigationLinkFieldsFragment,
  SeoContentHubPageBreadcrumbLinkFieldsFragment,
  SeoContentHubSubNavigationFieldsFragment,
  SeoContentHubTopLevelNavigationFieldsFragment,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import {
  CATEGORY_TYPE_NAME,
  DEFAULT_COUNTRY_CODE,
  PAGE_TYPE,
  SEO_CONTENT_PAGE_SLUG,
} from 'bundles/unified-seo-content-common/constants';
import type {
  CategoryTypeName,
  NavigationLink,
  NavigationLinkWithSubItems,
  SEOContentPageType,
  SeoContentFilter,
} from 'bundles/unified-seo-content-common/types';
import { getPageLink } from 'bundles/unified-seo-content-common/utils';

import _t from 'i18n!nls/articles';

type SubLinksForTopLevelLink = { [topLevelLinkSlug: string]: NavigationLink[] };

const isLocalizedPageData = (countryCode: CountryCode) => {
  return (categoryPageData: SeoContentHubPageBreadcrumbLinkFieldsFragment | null) =>
    countryCode === categoryPageData?.countryCode;
};

const getPageData = ({
  pageDataCollection,
  countryCode,
}: {
  pageDataCollection: Array<SeoContentHubPageBreadcrumbLinkFieldsFragment | null>;
  countryCode?: CountryCode;
}) => {
  const localizedPageData = countryCode && pageDataCollection?.find(isLocalizedPageData(countryCode));
  return localizedPageData ?? pageDataCollection?.find(isLocalizedPageData(DEFAULT_COUNTRY_CODE));
};

export function getCategoryBreadcrumbLink({
  pageData,
  countryCode,
  pageType,
}: {
  pageData: Pick<ArticleFieldsFragment, 'primaryCategory' | 'subCategoryCollection' | 'category'>;
  countryCode?: CountryCode;
  pageType: SEOContentPageType;
}): BreadcrumbLink[] {
  const { subCategoryCollection, primaryCategory } = pageData;

  const primaryCategoryPageDataCollection = primaryCategory?.pageDataCollection?.items;
  const subCategoryPageDataCollection = subCategoryCollection?.items?.[0]?.pageDataCollection?.items;

  const primaryCategoryPageData =
    primaryCategoryPageDataCollection &&
    getPageData({ pageDataCollection: primaryCategoryPageDataCollection, countryCode });
  const subCategoryPageData =
    subCategoryPageDataCollection && getPageData({ pageDataCollection: subCategoryPageDataCollection, countryCode });

  const primaryCategoryData = {
    name: primaryCategory?.name,
    ...primaryCategoryPageData,
  };

  const subCategoryData = {
    name: subCategoryCollection?.items?.[0]?.name,
    ...subCategoryPageData,
  };

  type BreadcrumbData = {
    name: string;
    slug: string;
    countryCode: CountryCode;
  };

  return [primaryCategoryData, subCategoryData]
    .filter((data): data is BreadcrumbData => !!data.name && !!data.slug)
    .map(({ slug, name, countryCode: localizedCountryCode }) => ({
      href: getPageLink({ pageType: PAGE_TYPE.categoryHub, localizedCountryCode, slug, seoContentPageType: pageType }),
      label: name,
    }));
}

export function toBreadcrumbs(origin: string, links: BreadcrumbLink[]): Breadcrumb[] {
  return links.map((link) => ({ name: link.label, absoluteUrl: path.join(origin, link.href) }));
}

export const getCategoryFilter = ({
  categoryName,
  categoryTypeName = CATEGORY_TYPE_NAME.primaryCategory,
}: {
  categoryName: string;
  categoryTypeName?: CategoryTypeName;
}): SeoContentFilter | null => {
  if (!categoryName) return null;
  const filters: SeoContentFilter = {};
  const categoryFilterByName = { name: categoryName };
  if (categoryTypeName === CATEGORY_TYPE_NAME.primaryCategory) {
    filters.primaryCategory = categoryFilterByName;
  } else if (categoryTypeName === CATEGORY_TYPE_NAME.subCategory) {
    filters.subCategory = categoryFilterByName;
  }
  return filters;
};

export const getNavigationLink = (pageData: SeoContentHubNavigationLinkFieldsFragment): NavigationLink | null => {
  const { slug, title, countryCode, pageType } = pageData || {};
  if (!slug || !title || !pageType) return null;
  return {
    label: title,
    slug,
    href: getPageLink({
      pageType: PAGE_TYPE.categoryHub,
      slug,
      localizedCountryCode: countryCode,
      seoContentPageType: pageType as SEOContentPageType,
    }),
    pageType: PAGE_TYPE.categoryHub,
  };
};

export const mapSubLinksForTopLevelLink = (subNavigationLinks?: SeoContentHubSubNavigationFieldsFragment[]) => {
  return subNavigationLinks?.reduce((accumulator: SubLinksForTopLevelLink, currentSubLink) => {
    const topLevelLinkSlug = currentSubLink?.categoryCollection?.items[0]?.pageDataCollection?.items[0]?.slug;
    const subLinkPageData = currentSubLink?.pageDataCollection?.items[0];
    if (!topLevelLinkSlug || !subLinkPageData) return accumulator;
    const subNavigationLink = getNavigationLink(subLinkPageData);
    if (!subNavigationLink) return accumulator;
    if (accumulator[topLevelLinkSlug])
      return { ...accumulator, [topLevelLinkSlug]: [...accumulator[topLevelLinkSlug], subNavigationLink] };
    else return { ...accumulator, [topLevelLinkSlug]: [subNavigationLink] };
  }, {});
};

export const getNavigationLinkWithSubItems = (
  topLevelNavigationLinks: SeoContentHubTopLevelNavigationFieldsFragment[],
  subLinksForTopLevelLink?: SubLinksForTopLevelLink
) => {
  return topLevelNavigationLinks.reduce((accumulator: NavigationLinkWithSubItems[], currentTopLevelLink) => {
    const currentTopLevelLinkPageData = currentTopLevelLink?.pageDataCollection?.items[0];
    if (!currentTopLevelLinkPageData) return accumulator;
    const topLevelNavigationLink = getNavigationLink(currentTopLevelLinkPageData);
    const topLevelLinkSlug = currentTopLevelLinkPageData.slug;
    if (!topLevelNavigationLink) return accumulator;
    if (subLinksForTopLevelLink && topLevelLinkSlug)
      return [
        ...accumulator,
        { ...topLevelNavigationLink, subNavigationLinks: subLinksForTopLevelLink[topLevelLinkSlug] },
      ];
    else accumulator.push(topLevelNavigationLink);
    return accumulator;
  }, []);
};

export const getNavigationLinks = (
  navigation: SeoContentHubNavigationFieldsFragment,
  additionalProps: {
    countryCode: CountryCode;
    pageType: SEOContentPageType;
  }
): NavigationLinkWithSubItems[] => {
  const topLevelNavigationLinks = navigation.topLevelCategoriesCollection?.items;
  if (!topLevelNavigationLinks) return [];
  const subNavigationLinks = navigation.subcategoriesCollection?.items;
  const filteredSubNavigationLinks = subNavigationLinks?.filter(
    (link): link is SeoContentHubSubNavigationFieldsFragment => !!link
  );
  const filteredTopLevelNavigationLinks = topLevelNavigationLinks?.filter(
    (link): link is SeoContentHubTopLevelNavigationFieldsFragment => !!link
  );

  const subLinksForTopLevelLink = subNavigationLinks
    ? mapSubLinksForTopLevelLink(filteredSubNavigationLinks)
    : undefined;
  const navigationLinksWithSubItems = getNavigationLinkWithSubItems(
    filteredTopLevelNavigationLinks,
    subLinksForTopLevelLink
  );

  const { pageType, countryCode } = additionalProps;
  // Add the main hub link to the top of the navigation, assuming the main hub exists
  const hubPageNavigationLink = {
    label: _t('Home'),
    slug: SEO_CONTENT_PAGE_SLUG[pageType],
    href: getPageLink({
      pageType: PAGE_TYPE.hubWithCategories,
      localizedCountryCode: countryCode,
      seoContentPageType: pageType,
    }),
    pageType: PAGE_TYPE.hubWithCategories,
  };

  return [hubPageNavigationLink, ...navigationLinksWithSubItems];
};
